/**
 * Edit a linked project (spec "Edit screen"; replaces Change account and Edit path). The account keys
 * as a set and the path, saved in ONE `warehouseProjects.setAccountKeys` call, which the server applies
 * in one transaction. A key the project links but no account has shows as removed and can only be
 * unticked.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/_shared/components/ui/form';
import { Input } from '@/_shared/components/ui/input';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import {
  accountKeyOptions,
  editProjectNote,
  type LinkedProject,
  type LinkedRepoAccount,
  projectPathLabel,
} from '../../lib/linkedRepoList';
import { AccountKeyPicker } from './AccountKeyPicker';
import {
  editProjectSchema,
  type EditProjectValues,
  normalizeProjectPath,
  rpcErrorCode,
  rpcErrorMessage,
} from './linkedRepoSchema';
import { useInvalidateLinkedRepos } from './useInvalidateLinkedRepos';

export const UNLINK_TO_REMOVE = 'Use Unlink to remove the project';

function valuesFor(project: LinkedProject | null): EditProjectValues {
  return {
    accountKeys: project ? project.keys.map((k) => k.accountKey) : [],
    projectPath: project ? projectPathLabel(project.projectPath) : '/',
  };
}

export function EditProjectDialog({
  project,
  onOpenChange,
  accounts,
  projects,
}: {
  /** The project being edited; null closes the dialog. */
  project: LinkedProject | null;
  onOpenChange: (open: boolean) => void;
  accounts: readonly LinkedRepoAccount[];
  projects: readonly LinkedProject[];
}): React.JSX.Element {
  const orpc = useOrpc();
  const invalidate = useInvalidateLinkedRepos();
  const form = useForm<EditProjectValues>({
    resolver: zodResolver(editProjectSchema),
    mode: 'onTouched',
    defaultValues: valuesFor(project),
  });
  const save = useMutation(orpc.warehouseProjects.setAccountKeys.mutationOptions());
  const pending = save.isPending;

  const groupId = project?.groupId ?? null;
  useEffect(() => {
    form.reset(valuesFor(project));
    save.reset();
    // Re-seed only when another project opens, not when a refetch hands the same project back.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed only when another project opens; the form and mutation are stable
  }, [groupId]);

  // The list refetches as Edit opens: when it brings a different project back and the form is
  // untouched, seed again so Save sends the set as it is now.
  const seedKey = project ? `${project.projectPath}|${project.keys.map((k) => k.accountKey).join(',')}` : '';
  useEffect(() => {
    if (project && !form.formState.isDirty) form.reset(valuesFor(project));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-seed on a changed project only; `form` is stable
  }, [seedKey]);

  const options = useMemo(() => accountKeyOptions(accounts, project?.keys ?? []), [accounts, project]);
  const keys = useWatch({ control: form.control, name: 'accountKeys' });
  const path = useWatch({ control: form.control, name: 'projectPath' });
  const pathChanged = !!project && normalizeProjectPath(path) !== projectPathLabel(project.projectPath);
  const keysChanged =
    !!project && (keys.length !== project.keys.length || project.keys.some((k) => !keys.includes(k.accountKey)));
  const note = project ? editProjectNote(project, keys, pathChanged, projects) : null;
  const noKeys = keys.length === 0;

  const submit = form.handleSubmit((values) => {
    if (!project) return;
    const newPath = normalizeProjectPath(values.projectPath);
    save.mutate(
      {
        repoFullName: project.repoFullName,
        projectPath: project.projectPath,
        accountKeys: values.accountKeys,
        // The keys the form was last seeded from (`form.reset` makes them the defaults): a set built from
        // a stale view — another admin added a key since — is refused as changed elsewhere.
        expectedAccountKeys: (form.formState.defaultValues?.accountKeys ?? []).filter(
          (key): key is string => typeof key === 'string',
        ),
        ...(pathChanged ? { newProjectPath: newPath } : {}),
      },
      {
        onSuccess: () => {
          invalidate();
          toast.success('Project saved.');
          onOpenChange(false);
        },
        onError: (err) => {
          const code = rpcErrorCode(err);
          // Changed elsewhere: refetch the list, so reopening Edit starts from the keys as they are now.
          if (code === 'CONFLICT') invalidate();
          // The path check on GitHub is the only BAD_REQUEST that names the path.
          if (code === 'BAD_REQUEST' && pathChanged && /path/i.test(err.message)) {
            form.setError('projectPath', { message: err.message });
          } else {
            form.setError('root', { message: rpcErrorMessage(err, 'Could not save the project.') });
          }
        },
      },
    );
  });

  const rootError = form.formState.errors.root?.message;

  return (
    <Dialog
      open={project !== null}
      onOpenChange={(next) => {
        if (!next && !pending) onOpenChange(false);
      }}
    >
      <DialogContent
        className="sm:max-w-lg"
        closeDisabled={pending}
        onEscapeKeyDown={(e) => {
          if (pending) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (pending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit project</DialogTitle>
          <DialogDescription className="min-w-0 break-words">
            {project ? `${project.repoFullName} at ${projectPathLabel(project.projectPath)}. ` : ''}
            Change where its project lives and which account keys it builds docs for.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form id="edit-project" noValidate onSubmit={(e) => void submit(e)} className="flex min-w-0 flex-col gap-4">
            <FormField
              control={form.control}
              name="projectPath"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project path</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="/" disabled={pending} />
                  </FormControl>
                  <FormDescription>
                    The folder that holds the project file. Use / for the repository root. A new path applies to every
                    account key.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="accountKeys"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account keys</FormLabel>
                  <FormDescription>
                    An account key is one warehouse database; every account that reads it shares the docs.
                  </FormDescription>
                  <FormControl>
                    <AccountKeyPicker
                      options={options}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={pending}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            {note && <p className="text-sm text-muted-foreground">{note}</p>}
            {rootError && <Callout variant="error">{rootError}</Callout>}
          </form>
        </Form>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={pending}
            onClick={() => onOpenChange(false)}
            label="Cancel"
          />
          <ActionTooltip show={noKeys} message={UNLINK_TO_REMOVE}>
            <Button
              type="submit"
              form="edit-project"
              variant="primary"
              loading={pending}
              disabled={noKeys || (!keysChanged && !pathChanged)}
              label={pending ? 'Saving…' : 'Save'}
            />
          </ActionTooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
